import { Injectable } from "@angular/core";
import { ServiceMessageBrokerFactory } from "shared/provider-webworker";

import {
  AlgoRiskTradeLookupService,
  BMAlgoRiskTradeLookupService,
  BMRiskTradeLookupService,
  LimitRiskTradeLookupService,
  RiskTradeLookupService,
} from "./RiskTradeLookupService";
import { PrimaryBlotterLookupService } from "../../impl/PrimaryBlotterLookupService";
import { ModelUpdate } from "../../impl/ModelUpdate";
import { IRiskTradePromiseLookupService } from "../IRiskTradePromiseLookupService";
import { DealPrivatizationService } from "../../../model/privatization/DealPrivatizationService";

import { CubiqWorker } from "../../../worker/CubiqWorker";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { RawDataDealAdapter } from "../../../mediator/adapter/RawDataDealAdapter";
import { NotificationService } from "../../notification/impl/NotificationService";

import { LoggerService } from "@bfx/logger";
import { TraderGroupPrimaryLookupService } from "./TraderGroupPrimaryLookupService";

const logger = LoggerService.for(__dirname);
@Injectable()
export abstract class RiskTradePrimaryLookupService
  extends PrimaryBlotterLookupService<string, CubiqTS.DealImpl>
  implements IRiskTradePromiseLookupService
{
  tradeDataObservable: any;
  protected abstract blotterProfile: string[];
  protected riskTraderGroups: Set<string> = new Set();

  constructor(
    base: RiskTradeLookupService,
    factory: ServiceMessageBrokerFactory,
    cubiqWorker: CubiqWorker,
    dataAdapter: RawDataDealAdapter,
    protected privatizationService: DealPrivatizationService,
    notificationService: NotificationService,
    protected traderGroupLookupService: TraderGroupPrimaryLookupService,
  ) {
    super(
      base,
      factory,
      CubiqTS.DealImpl,
      cubiqWorker,
      "DealImpl",
      CubiqTS.DealImpl,
      dataAdapter,
      notificationService,
    );

    let tgExternallyFilledPromise = new Promise<boolean>((res) => {
      this.traderGroupLookupService.isPrimed().then((isPrimed) => {
        if (!isPrimed) return res(isPrimed);

        this.traderGroupLookupService.getAll().then(
          (traderGroups) => {
            traderGroups.forEach((traderGroup) => {
              if (traderGroup.isExternallyFilledFg) {
                let id = traderGroup?.groupId?.toString();
                if (id != null && typeof id === "string") this.riskTraderGroups.add(id);
              }
            });
            res(true);
          },
          () => res(false),
        );
      });
    });

    let dependencyPromise: Promise<Array<any>> = Promise.all([
      this.privatizationService.isReady(),
      tgExternallyFilledPromise,
    ]);

    cubiqWorker.isAuth(`${this.constructor.name}`).then(() => {
      this.tradeDataObservable = cubiqWorker
        .primeDataProfile(this.blotterProfile, CubiqTS.DealImpl.name)
        .subscribe({
          next: (riskTrade: CubiqTS.DealImpl[]) => {
            dependencyPromise.then((_: Array<any>) => {
              riskTrade = riskTrade
                .filter((o) => o != null && this.isPrimeEligible(o))
                .map((o) => this.privatizationService.checkPrivatization(o));
              this.base.prime(riskTrade, false);
            });
          },
          error: (e: Error) => {
            logger.out.error("Failed to prime trade data: " + e);
            this._primed.reject(e);
          },
          complete: () => {
            dependencyPromise.then(
              () => {
                this.postProcessRiskTrades();
                this.base.prime([], true);
                this._primed.resolve(true);
              },
              (err) => {
                this._primed.reject(err);
              },
            );
          },
        });
    });

    this.cubiqWorker
      .getEncodableObservable<CubiqTS.DealImpl>(CubiqTS.DealImpl.name)
      .subscribe((riskTrade: CubiqTS.DealImpl) => {
        dependencyPromise.then(() => {
          try {
            this.processRiskTrade(riskTrade);
          } catch (e) {
            logger.out.error("Error processing trade data event: " + e);
          }
        });
      });
  }

  protected processRiskTrade(riskTrade: CubiqTS.DealImpl): void {
    if (
      riskTrade.dealStateId !== undefined &&
      riskTrade.dealStateId !== null &&
      riskTrade.dealStateId !== CubiqTS.OrderDealState.CANCELLED &&
      riskTrade.dealStateId !== CubiqTS.OrderDealState.FILL_CANCEL
    ) {
      let existingRiskTrade = this.base.getItem(this.base.getItemKey(riskTrade));
      if (
        existingRiskTrade &&
        existingRiskTrade.dealStateId !== CubiqTS.OrderDealState.ACTIVE &&
        existingRiskTrade.dealStateId !== CubiqTS.OrderDealState.FILL_PENDING &&
        riskTrade.dealStateId === CubiqTS.OrderDealState.ACTIVE
      ) {
        // Deals can arrive out of sequence (https://www.bcdfx.com/ijira/browse/OMS-20851) so ignore any
        // ACTIVE deals when existing order is not ACTIVE or FILL_PENDING
        return;
      } else {
        if (this.isPrimeEligible(riskTrade) && this.filterEvent(riskTrade)) {
          riskTrade = this.privatizationService.checkPrivatization(riskTrade);
          this.base.mapItem(riskTrade);
          this.fireModelUpdateEvent(riskTrade);
        }
      }
    } else {
      riskTrade = this.privatizationService.checkPrivatization(riskTrade);
      this.fireModelDeleteEvent(riskTrade.id.toString());
    }
  }

  protected abstract filterEvent(riskTrade: CubiqTS.ConcreteEncodable): boolean;

  protected keyProperty(): string {
    return "id";
  }

  protected createModelUpdateEvent(updatedItem: CubiqTS.DealImpl): any {
    return updatedItem;
  }

  @ModelUpdate(CubiqTS.ClientImpl)
  protected onClientUpdate(client: CubiqTS.ClientImpl, deal: CubiqTS.DealImpl): boolean {
    return deal.clientCode == client.clientId;
  }

  @ModelUpdate(CubiqTS.AccountImpl)
  protected onAccountUpdate(account: CubiqTS.AccountImpl, deal: CubiqTS.DealImpl): boolean {
    return deal.accountCode == account.id;
  }

  @ModelUpdate(CubiqTS.CurrencyImpl)
  protected onCurrencyUpdate(currency: CubiqTS.CurrencyImpl, deal: CubiqTS.DealImpl): boolean {
    return deal.productId.startsWith(currency.id) || deal.productId.endsWith(currency.id);
  }

  @ModelUpdate(CubiqTS.CurrencyPairImpl)
  protected onCurrencyPairUpdate(
    currencyPair: CubiqTS.CurrencyPairImpl,
    deal: CubiqTS.DealImpl,
  ): boolean {
    let pair: string = currencyPair.base + currencyPair.nonBase,
      inverse: string = currencyPair.nonBase + currencyPair.base;
    return deal.productId == pair || deal.productId == inverse;
  }

  @ModelUpdate(CubiqTS.TradingGroupImpl)
  protected onTraderGroupUpdate(
    traderGroup: CubiqTS.TradingGroupImpl,
    deal: CubiqTS.DealImpl,
  ): boolean {
    if (traderGroup.isExternallyFilledFg) {
      let id = traderGroup?.groupId?.toString();
      if (id != null && typeof id === "string") this.riskTraderGroups.add(id);
      else this.riskTraderGroups.delete(id);
    }

    return this.compareIds(deal.traderGroupId, traderGroup.groupId);
  }

  @ModelUpdate(CubiqTS.SalesGroupImpl)
  protected onSalesGroupUpdate(
    salesGroup: CubiqTS.SalesGroupImpl,
    deal: CubiqTS.DealImpl,
  ): boolean {
    return this.compareIds(deal.salesGroupId, salesGroup.groupId);
  }

  @ModelUpdate(CubiqTS.UserImpl)
  protected onUserUpdate(user: CubiqTS.UserImpl, deal: CubiqTS.DealImpl): boolean {
    return (
      deal.entryUserCode == user.userCode ||
      deal.fillUserCode == user.userCode ||
      this.compareIds(deal.confirmedSalesUserId, user.userId)
    );
  }
  public isPrimeEligible(_: CubiqTS.DealImpl): boolean {
    return true;
  }

  public postProcessRiskTrades(): void {
    // no-op
  }

  protected isRiskTrade(deal: CubiqTS.DealImpl): boolean {
    return deal.makerDealRef != null || deal.spotReservationFg === true;
  }
}

@Injectable()
export class LimitRiskTradePrimaryLookupService extends RiskTradePrimaryLookupService {
  protected blotterProfile: string[] = ["Risk Trade Blotter Limit"];

  constructor(
    base: LimitRiskTradeLookupService,
    factory: ServiceMessageBrokerFactory,
    cubiqWorker: CubiqWorker,
    dataAdapter: RawDataDealAdapter,
    privatizationService: DealPrivatizationService,
    notificationService: NotificationService,
    traderGroupLookupService: TraderGroupPrimaryLookupService,
  ) {
    super(
      base,
      factory,
      cubiqWorker,
      dataAdapter,
      privatizationService,
      notificationService,
      traderGroupLookupService,
    );
  }

  protected filterEvent(riskTrade: CubiqTS.ConcreteEncodable): boolean {
    return (
      riskTrade instanceof CubiqTS.DealImpl &&
      riskTrade.encodableType === "com.bcdfx.model.DealImpl" &&
      riskTrade.dealStateId !== CubiqTS.OrderDealState.CANCELLED &&
      riskTrade.dealStateId !== CubiqTS.OrderDealState.FILL_CANCEL &&
      (riskTrade.orderType === CubiqTS.OrderType.LIMIT_TP ||
        riskTrade.orderType === CubiqTS.OrderType.LIMIT_SL ||
        riskTrade.orderType === CubiqTS.OrderType.LIMIT_CALL ||
        riskTrade.orderType === CubiqTS.OrderType.MARKET) &&
      (riskTrade.productType === "SPT" ||
        riskTrade.productType === "FWD" ||
        riskTrade.productType === "NDF") &&
      (riskTrade.strategyType === CubiqTS.StrategyType.STANDARD ||
        riskTrade.strategyType === CubiqTS.StrategyType.GAMMA) &&
      this.isRiskTrade(riskTrade)
    );
  }

  public isPrimeEligible(deal: CubiqTS.DealImpl): boolean {
    return true;
  }
}

@Injectable()
export class AlgoRiskTradePrimaryLookupService extends RiskTradePrimaryLookupService {
  protected blotterProfile: string[] = ["Risk Trade Blotter Algo"];

  constructor(
    base: AlgoRiskTradeLookupService,
    factory: ServiceMessageBrokerFactory,
    cubiqWorker: CubiqWorker,
    dataAdapter: RawDataDealAdapter,
    privatizationService: DealPrivatizationService,
    notificationService: NotificationService,
    traderGroupLookupService: TraderGroupPrimaryLookupService,
  ) {
    super(
      base,
      factory,
      cubiqWorker,
      dataAdapter,
      privatizationService,
      notificationService,
      traderGroupLookupService,
    );
  }

  protected filterEvent(riskTrade: CubiqTS.ConcreteEncodable): boolean {
    return (
      riskTrade instanceof CubiqTS.DealImpl &&
      riskTrade.encodableType === "com.bcdfx.model.DealImpl" &&
      riskTrade.dealStateId !== CubiqTS.OrderDealState.CANCELLED &&
      riskTrade.dealStateId !== CubiqTS.OrderDealState.FILL_CANCEL &&
      riskTrade.orderType === CubiqTS.OrderType.ALGO &&
      (riskTrade.productType === "SPT" ||
        riskTrade.productType === "FWD" ||
        riskTrade.productType === "NDF") &&
      (riskTrade.strategyType === CubiqTS.StrategyType.STANDARD ||
        riskTrade.strategyType === CubiqTS.StrategyType.GAMMA) &&
      this.isRiskTrade(riskTrade)
    );
  }

  public isPrimeEligible(deal: CubiqTS.DealImpl): boolean {
    return true;
  }
}

@Injectable()
export class BMRiskTradePrimaryLookupService extends RiskTradePrimaryLookupService {
  protected blotterProfile: string[] = ["Risk Trade Blotter BM"];

  constructor(
    base: BMRiskTradeLookupService,
    factory: ServiceMessageBrokerFactory,
    cubiqWorker: CubiqWorker,
    dataAdapter: RawDataDealAdapter,
    privatizationService: DealPrivatizationService,
    notificationService: NotificationService,
    traderGroupLookupService: TraderGroupPrimaryLookupService,
  ) {
    super(
      base,
      factory,
      cubiqWorker,
      dataAdapter,
      privatizationService,
      notificationService,
      traderGroupLookupService,
    );
  }

  protected filterEvent(riskTrade: CubiqTS.ConcreteEncodable): boolean {
    return (
      riskTrade instanceof CubiqTS.DealImpl &&
      riskTrade.encodableType === "com.bcdfx.model.DealImpl" &&
      riskTrade.dealStateId !== CubiqTS.OrderDealState.CANCELLED &&
      riskTrade.dealStateId !== CubiqTS.OrderDealState.FILL_CANCEL &&
      riskTrade.orderType === CubiqTS.OrderType.BENCHMARK &&
      riskTrade.productType === "SPT" &&
      this.isRiskTrade(riskTrade)
    );
  }
}

/**
 * This will probably be removed when we introduce BM Algos in the risk trade blotter
 * as we only need one BM risk trade service
 */
@Injectable()
export class BMAlgoTradePrimaryLookupService extends RiskTradePrimaryLookupService {
  protected blotterProfile: string[] = [];

  constructor(
    base: BMAlgoRiskTradeLookupService,
    factory: ServiceMessageBrokerFactory,
    cubiqWorker: CubiqWorker,
    dataAdapter: RawDataDealAdapter,
    privatizationService: DealPrivatizationService,
    notificationService: NotificationService,
    traderGroupLookupService: TraderGroupPrimaryLookupService,
  ) {
    super(
      base,
      factory,
      cubiqWorker,
      dataAdapter,
      privatizationService,
      notificationService,
      traderGroupLookupService,
    );
  }

  protected filterEvent(riskTrade: CubiqTS.ConcreteEncodable): boolean {
    return (
      riskTrade instanceof CubiqTS.DealImpl &&
      riskTrade.encodableType === "com.bcdfx.model.DealImpl" &&
      riskTrade.dealStateId !== CubiqTS.OrderDealState.CANCELLED &&
      riskTrade.dealStateId !== CubiqTS.OrderDealState.FILL_CANCEL &&
      riskTrade.orderType === CubiqTS.OrderType.BMALGO &&
      riskTrade.productType === "SPT" &&
      this.isRiskTrade(riskTrade)
    );
  }
}
